
import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { Ticket } from '../../types';
import { getStatusLabel } from '../../utils/helpers';

interface AnalyticsDashboardProps {
    tickets: Ticket[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
const STATUS_COLORS: Record<string, string> = {
    'OPEN': '#3b82f6', // blue
    'IN_PROGRESS': '#a855f7', // purple
    'WAITING_INFO': '#eab308', // yellow
    'RESOLVED': '#22c55e', // green
    'CLOSED': '#64748b', // slate
};

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ tickets }) => {

    // 1. Tickets by Status (Pie Chart)
    const statusData = useMemo(() => {
        const counts: Record<string, number> = {};
        tickets.forEach(t => {
            counts[t.status] = (counts[t.status] || 0) + 1;
        });
        return Object.keys(counts).map(key => ({
            name: getStatusLabel(key),
            value: counts[key],
            key: key
        }));
    }, [tickets]);

    // 2. Tickets by Priority (Bar Chart)
    const priorityData = useMemo(() => {
        const counts: Record<string, number> = { 'P1': 0, 'P2': 0, 'P3': 0, 'P4': 0 };
        tickets.forEach(t => {
            if (counts[t.priority] !== undefined) counts[t.priority]++;
        });
        return Object.keys(counts).map(key => ({
            name: key,
            tickets: counts[key]
        }));
    }, [tickets]);

    // 3. TicketsTrend (Last 7 days)
    const trendData = useMemo(() => {
        const last7Days = [...Array(7)].map((_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - i);
            return d.toISOString().split('T')[0];
        }).reverse();

        return last7Days.map(date => {
            const count = tickets.filter(t =>
                new Date(t.createdAt).toISOString().split('T')[0] === date
            ).length;

            // Format date for display (e.g., "Jan 28")
            const displayDate = new Date(date).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });

            return {
                date: displayDate,
                tickets: count
            };
        });
    }, [tickets]);

    // 4. Tickets by Category (Bar Chart Horizontal)
    const categoryData = useMemo(() => {
        const counts: Record<string, number> = {};
        tickets.forEach(t => {
            counts[t.category] = (counts[t.category] || 0) + 1;
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1]) // Sort desc
            .slice(0, 5) // Top 5
            .map(([name, value]) => ({ name, value }));
    }, [tickets]);

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <h2 className="text-xl font-bold text-gray-800">Análisis y Métricas</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Status Distribution */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                    <h3 className="text-lg font-bold mb-4 text-gray-700">Estado de Tickets</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {statusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.key] || COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Trend Chart */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                    <h3 className="text-lg font-bold mb-4 text-gray-700">Tendencia (Últimos 7 días)</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                                <defs>
                                    <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Area type="monotone" dataKey="tickets" stroke="#8884d8" fillOpacity={1} fill="url(#colorTickets)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Priority Distribution */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                    <h3 className="text-lg font-bold mb-4 text-gray-700">Tickets por Prioridad</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={priorityData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="tickets" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Categories */}
                <div className="bg-white p-6 rounded-xl shadow-sm border">
                    <h3 className="text-lg font-bold mb-4 text-gray-700">Top 5 Categorías</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart layout="vertical" data={categoryData} margin={{ left: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} name="Tickets" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};
